import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Save,
  User,
  Compass,
  Sparkles,
  Clapperboard,
  Sliders,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState } from "@/components/states";
import { ScoreMeter } from "@/components/ScoreMeter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChipSelect } from "@/components/ChipSelect";
import { TagInput } from "@/components/TagInput";
import * as O from "@/pages/onboarding/options";

function Section({ title, description, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-border/60 pb-4 mb-5">
        {Icon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon className="h-4 w-4" />
          </div>
        )}
        <div>
          <h2 className="font-display text-base font-bold text-foreground">{title}</h2>
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

function Group({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">{label}</label>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export default function Profile() {
  const [state, setState] = useState({ loading: true, error: null });
  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [dna, setDna] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const { data } = await api.get("/profile");
      const p = data.profile || {};
      const normalizedForm = {
        creator_name: p.creator_name || "",
        location: p.location || "",
        age_range: p.age_range || null,
        language: p.language || null,
        interests: p.interests || [],
        skills: p.skills || [],
        expertise: p.expertise || [],
        subjects: p.subjects || [],
        experience_level: p.experience_level || null,
        content_styles: p.content_styles || [],
        content_formats: p.content_formats || [],
        goals: p.goals || [],
        constraints: {
          creation_time: p.constraints?.creation_time || null,
          editing_skill: p.constraints?.editing_skill || null,
          equipment_level: p.constraints?.equipment_level || null,
          budget: p.constraints?.budget || null,
          preferred_platforms: p.constraints?.preferred_platforms || [],
        },
        niche: {
          mode: p.niche?.mode || (p.niche?.value ? "specific" : "suggested"),
          value: p.niche?.value || "",
          categories: p.niche?.categories || [],
        },
      };
      setForm(normalizedForm);
      setInitialForm(JSON.parse(JSON.stringify(normalizedForm)));
      setDna(data.dna);
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: apiErrorMessage(err) });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setConstraint = (patch) =>
    setForm((f) => ({ ...f, constraints: { ...f.constraints, ...patch } }));
  const setNiche = (patch) =>
    setForm((f) => ({ ...f, niche: { ...f.niche, ...patch } }));

  const hasUnsavedChanges =
    initialForm && JSON.stringify(form) !== JSON.stringify(initialForm);

  const resetChanges = () => {
    if (initialForm) {
      setForm(JSON.parse(JSON.stringify(initialForm)));
      toast.info("Changes reverted");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setDna(data.dna);
      setInitialForm(JSON.parse(JSON.stringify(form)));
      toast.success("Creator DNA and settings updated successfully!");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (state.loading)
    return (
      <AppShell title="Creator Profile">
        <Loader label="Loading creator profile" />
      </AppShell>
    );

  if (state.error)
    return (
      <AppShell title="Creator Profile">
        <ErrorState message={state.error} onRetry={load} />
      </AppShell>
    );

  return (
    <AppShell title="Creator Profile">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Account Settings</span>
            {hasUnsavedChanges && (
              <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-semibold text-accent" data-testid="profile-unsaved-badge">
                Unsaved changes
              </span>
            )}
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold text-foreground">Creator DNA & Setup</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Update your niche, language (including Hinglish), style, constraints, and expertise. This powers your AI script writer, hooks, and opportunities.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <Button variant="ghost" size="sm" onClick={resetChanges} disabled={saving} data-testid="profile-reset">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Revert
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving}
            data-testid="profile-save"
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm"
          >
            <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* SIDEBAR: Completeness & Quick Summary */}
        <div className="order-2 md:order-1 space-y-6 md:col-span-1">
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm space-y-4">
            <ScoreMeter
              testid="profile-completeness"
              label="Profile completeness"
              value={dna?.completeness?.score || 0}
              sublabel={`${dna?.completeness?.filled || 0}/${dna?.completeness?.total || 9} sections complete`}
            />

            {dna?.completeness?.sections && (
              <div className="border-t border-border/60 pt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  DNA Sections
                </p>
                {Object.entries(dna.completeness.sections).map(([key, filled]) => (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <span className="capitalize text-foreground">{key.replace(/_/g, " ")}</span>
                    {filled ? (
                      <span className="flex items-center gap-1 text-accent font-medium">
                        <CheckCircle2 className="h-3 w-3" /> Ready
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <AlertCircle className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-surface p-5 text-xs text-muted-foreground space-y-3">
            <p className="font-semibold uppercase tracking-wider text-foreground">How Virality Uses This</p>
            <p>
              Your <strong className="text-foreground">Language</strong> and <strong className="text-foreground">Tone</strong> dictate the AI Script Generator and Hook Writer (including Hinglish and cultural context).
            </p>
            <p>
              Your <strong className="text-foreground">Niche</strong> and <strong className="text-foreground">Constraints</strong> filter opportunities and match video formats to your actual recording workflow.
            </p>
          </div>
        </div>

        {/* MAIN EDITABLE SECTIONS */}
        <div className="order-1 md:order-2 space-y-6 md:col-span-2">
          {/* 1. Identity & Language */}
          <Section title="Identity & Language" icon={User} description="Basic creator identity and spoken languages">
            <Group label="Creator name" hint="Public handle or channel name">
              <Input
                value={form.creator_name}
                onChange={(e) => set({ creator_name: e.target.value })}
                placeholder="e.g. Sonik Gamerz or Tech Journey"
                data-testid="pf-name"
              />
            </Group>
            <Group label="Location" hint="City or Country">
              <Input
                value={form.location}
                onChange={(e) => set({ location: e.target.value })}
                placeholder="e.g. Mumbai, India or San Francisco, USA"
                data-testid="pf-location"
              />
            </Group>
            <Group label="Primary Language" hint="AI scripts & hooks will be written in this language">
              <ChipSelect
                single
                options={O.LANGUAGES}
                value={form.language}
                onChange={(v) => set({ language: v })}
                testid="pf-language"
              />
            </Group>
            <Group label="Age range">
              <ChipSelect
                single
                options={O.AGE_RANGES}
                value={form.age_range}
                onChange={(v) => set({ age_range: v })}
                testid="pf-age"
              />
            </Group>
          </Section>

          {/* 2. Content Niche */}
          <Section title="Content Niche & Focus" icon={Compass} description="Where you want to win and establish authority">
            <Group label="Your specific niche" hint="The exact topic or angle of your channel">
              <Input
                value={form.niche.value}
                onChange={(e) => setNiche({ value: e.target.value, mode: "specific" })}
                placeholder="e.g. Minecraft Addons & Modding or AI Coding Tools"
                data-testid="pf-niche"
              />
            </Group>
            <Group label="Niche Categories" hint="Broad categories you cover">
              <ChipSelect
                options={O.NICHE_CATEGORIES}
                value={form.niche.categories}
                onChange={(v) => setNiche({ categories: v })}
                testid="pf-niche-categories"
              />
            </Group>
          </Section>

          {/* 3. Interests & Expertise */}
          <Section title="Interests, Skills & Experience" icon={Sparkles} description="Your background and topics you can credibly discuss">
            <Group label="Experience level">
              <ChipSelect
                single
                options={O.EXPERIENCE_LEVELS}
                value={form.experience_level}
                onChange={(v) => set({ experience_level: v })}
                testid="pf-experience"
              />
            </Group>
            <Group label="Topics of Interest" hint="Select all that apply">
              <ChipSelect
                options={O.INTERESTS}
                value={form.interests}
                onChange={(v) => set({ interests: v })}
                testid="pf-interests"
              />
            </Group>
            <Group label="Specific Skills" hint="Press Enter or add commas to add tags">
              <TagInput
                value={form.skills}
                onChange={(v) => set({ skills: v })}
                placeholder="e.g. Python, Blender, Video Editing, Speedrunning"
                testid="pf-skills"
              />
            </Group>
            <Group label="Areas of Expertise" hint="Topics you master deeply">
              <TagInput
                value={form.expertise}
                onChange={(v) => set({ expertise: v })}
                placeholder="e.g. Bedrock Addons, Shader packs, Prompt Engineering"
                testid="pf-expertise"
              />
            </Group>
            <Group label="Subjects you frequently cover">
              <TagInput
                value={form.subjects}
                onChange={(v) => set({ subjects: v })}
                placeholder="e.g. Tutorials, Comparisons, Mod Showcases, Beginner Guides"
                testid="pf-subjects"
              />
            </Group>
          </Section>

          {/* 4. Style, Formats & Goals */}
          <Section title="Content Style & Formats" icon={Clapperboard} description="How you show up on camera and video structures">
            <Group label="Content Styles & Tone" hint="Select all that apply">
              <ChipSelect
                options={O.CONTENT_STYLES}
                value={form.content_styles}
                onChange={(v) => set({ content_styles: v })}
                testid="pf-styles"
              />
            </Group>
            <Group label="Content Formats">
              <ChipSelect
                options={O.CONTENT_FORMATS}
                value={form.content_formats}
                onChange={(v) => set({ content_formats: v })}
                testid="pf-formats"
              />
            </Group>
            <Group label="Creator Goals" hint="What are you optimizing for?">
              <ChipSelect
                options={O.GOALS}
                value={form.goals}
                onChange={(v) => set({ goals: v })}
                testid="pf-goals"
              />
            </Group>
          </Section>

          {/* 5. Production Constraints & Equipment */}
          <Section title="Creation Constraints & Platforms" icon={Sliders} description="Your realistic time, budget, and gear">
            <Group label="Preferred platforms">
              <ChipSelect
                options={O.PLATFORMS}
                value={form.constraints.preferred_platforms}
                onChange={(v) => setConstraint({ preferred_platforms: v })}
                testid="pf-platforms"
              />
            </Group>
            <Group label="Available creation time per week">
              <ChipSelect
                single
                options={O.CREATION_TIME}
                value={form.constraints.creation_time}
                onChange={(v) => setConstraint({ creation_time: v })}
                testid="pf-time"
              />
            </Group>
            <Group label="Editing skill level">
              <ChipSelect
                single
                options={O.EDITING_SKILL}
                value={form.constraints.editing_skill}
                onChange={(v) => setConstraint({ editing_skill: v })}
                testid="pf-editing"
              />
            </Group>
            <Group label="Equipment level">
              <ChipSelect
                single
                options={O.EQUIPMENT_LEVEL}
                value={form.constraints.equipment_level}
                onChange={(v) => setConstraint({ equipment_level: v })}
                testid="pf-equipment"
              />
            </Group>
            <Group label="Monthly budget">
              <ChipSelect
                single
                options={O.BUDGET}
                value={form.constraints.budget}
                onChange={(v) => setConstraint({ budget: v })}
                testid="pf-budget"
              />
            </Group>
          </Section>

          {/* Bottom Save Action */}
          <div className="flex items-center justify-end gap-3 rounded-2xl border border-border bg-surface p-4 shadow-sm">
            {hasUnsavedChanges && (
              <Button variant="ghost" onClick={resetChanges} disabled={saving}>
                <RotateCcw className="mr-1.5 h-4 w-4" /> Revert changes
              </Button>
            )}
            <Button
              onClick={save}
              disabled={saving}
              className="bg-accent text-accent-foreground hover:bg-accent-hover px-6 shadow-sm"
              data-testid="profile-bottom-save"
            >
              <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save all changes"}
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
